import React, { useEffect, useState } from "react";
import { CONDITIONS } from "@/data/mock";
import LocationPicker from "@/components/common/LocationPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { SlidersHorizontal, X } from "lucide-react";

export default function FiltersSidebar({ filters, setFilters, subcategories = [] }) {
  const [open, setOpen] = useState(false);
  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => setFilters({
    county: "", city: "", subcategory: "", minPrice: "", maxPrice: "", conditions: [], onlyFeatured: false, sort: "newest",
  });

  // Close mobile drawer on resize to desktop
  useEffect(() => {
    const onR = () => { if (window.innerWidth >= 1024) setOpen(false); };
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  const toggleCondition = (c) => {
    update("conditions", filters.conditions.includes(c) ? filters.conditions.filter((x) => x !== c) : [...filters.conditions, c]);
  };

  const Content = (
    <div className="space-y-5">
      {subcategories.length > 0 && (
        <div>
          <Label className="text-white text-xs uppercase tracking-wider">Subcategorie</Label>
          <Select value={filters.subcategory || "all"} onValueChange={(v) => update("subcategory", v === "all" ? "" : v)}>
            <SelectTrigger className="mt-2 amd-input h-10"><SelectValue placeholder="Toate" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate</SelectItem>
              {subcategories.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label className="text-white text-xs uppercase tracking-wider">Locație</Label>
        <div className="mt-2">
          <LocationPicker
            county={filters.county || ""}
            city={filters.city || ""}
            onChange={(county, city) => setFilters((f) => ({ ...f, county, city }))}
            searchMode
            className="grid grid-cols-1 gap-2"
            testIdPrefix="filter"
          />
        </div>
      </div>
      <div>
        <Label className="text-white text-xs uppercase tracking-wider">Preț (lei)</Label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Input type="number" placeholder="Min" value={filters.minPrice} onChange={(e) => update("minPrice", e.target.value)} className="amd-input h-10" />
          <Input type="number" placeholder="Max" value={filters.maxPrice} onChange={(e) => update("maxPrice", e.target.value)} className="amd-input h-10" />
        </div>
      </div>
      <div>
        <Label className="text-white text-xs uppercase tracking-wider">Stare</Label>
        <div className="mt-2 space-y-2">
          {CONDITIONS.map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--amd-text-muted)" }}>
              <Checkbox checked={filters.conditions.includes(c)} onCheckedChange={() => toggleCondition(c)} />
              <span>{c}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--amd-text-muted)" }}>
          <Checkbox checked={filters.onlyFeatured} onCheckedChange={(v) => update("onlyFeatured", !!v)} />
          <span>Doar anunțuri promovate</span>
        </label>
      </div>
      <div>
        <Label className="text-white text-xs uppercase tracking-wider">Sortare</Label>
        <Select value={filters.sort} onValueChange={(v) => update("sort", v)}>
          <SelectTrigger className="mt-2 amd-input h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Cele mai noi</SelectItem>
            <SelectItem value="priceAsc">Preț crescător</SelectItem>
            <SelectItem value="priceDesc">Preț descrescător</SelectItem>
            <SelectItem value="popular">Cele mai vizualizate</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" onClick={reset} className="w-full border-white/20 text-white hover:bg-white/5">
        Resetează filtrele
      </Button>
    </div>
  );

  return (
    <>
      {/* Mobile trigger */}
      <div className="lg:hidden mb-3">
        <Button onClick={() => setOpen(true)} className="amd-btn-primary gap-2 w-full sm:w-auto">
          <SlidersHorizontal className="w-4 h-4" /> Filtre
        </Button>
      </div>
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0">
        <div className="amd-card rounded-lg p-4 sticky top-32">
          <div className="font-black text-white mb-4 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" />Filtre</div>
          {Content}
        </div>
      </aside>
      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden" role="dialog">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm p-4 overflow-y-auto fade-in" style={{ background: "var(--amd-surface)" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-black text-white">Filtre</div>
              <button onClick={() => setOpen(false)} className="p-2 text-white"><X className="w-5 h-5" /></button>
            </div>
            {Content}
            <Button onClick={() => setOpen(false)} className="amd-btn-primary w-full mt-5">Aplică</Button>
          </div>
        </div>
      )}
    </>
  );
}
